import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Calendar, Compass, ShieldCheck, Heart, MapPin, Award, ShieldAlert, Cpu } from 'lucide-react';
import ImageGallery from '@/components/ImageGallery';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';

const SupplierGalantinoPage = () => {
  const { language } = useLanguage();
  const timelineEvents = [
    {
      year: "1926",
      title: language === 'PL' ? "Założenie Frantoio" : "Founding of Frantoio",
      icon: <Calendar className="w-5 h-5 text-emerald-600" />,
      text: language === 'PL' ? "Vito Galantino, wybitny ekspert i certyfikowany degustator oliwy, kupuje zabytkowy, XIX-wieczny młyn zbożowy w Bisceglie i z pasją przekształca go w nowoczesną tłocznię oliwy z oliwek (Frantoio)." : "Vito Galantino, an outstanding expert and certified oil taster, buys a historic 19th-century grain mill in Bisceglie and passionately transforms it into a modern olive oil mill (Frantoio).",
      image: "/suppliers/galantino/workers-history.webp",
      imgAlt: "Historyczna tłocznia Galantino"
    },
    {
      year: "1960",
      title: language === 'PL' ? "Posiadłość „La Fenice”" : "\"La Fenice\" estate",
      icon: <Compass className="w-5 h-5 text-emerald-600" />,
      text: language === 'PL' ? "Rodzina Galantino nabywa posiadłość „La Fenice” w Apulii – rozległe, zielone pola o powierzchni 47 hektarów gajów oliwnych z ponad 15 000 drzewami odmian Ogliarola i Coratina. Od tej pory firma kontroluje pełny cykl produkcyjny: od uprawy po butelkowanie." : "The Galantino family acquires the \"La Fenice\" estate in Apulia - vast, green fields covering 47 hectares of olive groves with over 15,000 trees of the Ogliarola and Coratina varieties. From then on, the company controls the full production cycle: from cultivation to bottling.",
      image: "/suppliers/galantino/harvester.webp",
      imgAlt: "Gaje oliwne La Fenice"
    },
    {
      year: "1985",
      title: language === 'PL' ? "Wejście na rynek premium" : "Entering the premium market",
      icon: <Award className="w-5 h-5 text-emerald-600" />,
      text: language === 'PL' ? "Michele Galantino wprowadza markę do globalnego sektora premium. Butelkuje oliwę w ekskluzywne, designerskie opakowania, w tym słynną, ręcznie malowaną ceramikę apulijską stworzoną przez lokalnych artystów, która staje się kultowym symbolem marki." : "Michele Galantino introduces the brand to the global premium sector. He bottles the oil in exclusive, designer packaging, including the famous, hand-painted Apulian ceramics created by local artists, which becomes the brand's iconic symbol."
    },
    {
      year: language === 'PL' ? "Obecnie" : "Currently",
      title: language === 'PL' ? "Globalna renoma" : "Global reputation",
      icon: <Heart className="w-5 h-5 text-emerald-600" />,
      text: language === 'PL' ? "Dziś marką zarządza Gianvito Galantino. Luksusowa oliwa Galantino dostępna jest w najbardziej prestiżowych „butikach smaku” na całym świecie, regularnie zdobywając najważniejsze międzynarodowe nagrody za jakość i design." : "Today the brand is managed by Gianvito Galantino. Luxury Galantino oil is available in the most prestigious \"taste boutiques\" around the world, regularly winning major international awards for quality and design."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F3F4ED] text-[#2C302E]">
      <SEO 
        title={language === 'PL' ? "Frantoio Galantino - Włoska Oliwa z Oliwek | Włoska Piwnica" : "Frantoio Galantino - Italian Olive Oil | Włoska Piwnica"}
        description={language === 'PL' ? "Odkryj tajemnice produkcji włoskiej oliwy najwyższej jakości od Frantoio Galantino. Tradycja i pasja od 1926 roku." : "Discover the secrets of Italian premium olive oil production by Frantoio Galantino. Tradition and passion since 1926."}
        canonicalUrl="/dostawcy/galantino"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Frantoio Galantino",
          "url": "https://wloska-piwnica.pl/dostawcy/galantino",
          "logo": "https://wloska-piwnica.pl/suppliers/galantino/logo.svg",
          "description": "Producent najwyższej jakości oliwy z oliwek extra virgin z Apulii od 1926 roku.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Apulia",
            "addressCountry": "IT"
          }
        }}
      />
      <Header />
      
      <main className="flex-grow pt-36 md:pt-40 lg:pt-44 pb-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-[#2D3E35] text-white border-b border-[#ebdcb9]/20">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" style={{ backgroundImage: `url('/suppliers/galantino/harvester.webp')` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2D3E35]/60 to-[#2D3E35]" />
          
          <div className="container relative z-10 mx-auto px-4 max-w-5xl text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-8"
            >
              <img 
                src="/suppliers/galantino/logo-mill.webp" 
                alt="Galantino Logo" 
                className="h-24 md:h-32 object-contain bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 shadow-xl"
              />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-6xl font-display font-bold mb-4 tracking-wide text-[#C7D3C4]"
            >
              Frantoio Galantino
            </motion.h1>
            
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="h-0.5 w-32 bg-[#A3B19B] mx-auto rounded-full mb-6"
            />
            
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg md:text-xl text-[#F4F6F0]/80 font-serif max-w-2xl mx-auto italic"
            >
              {language === 'PL' ? "„Mastri Oleari dal 1926 – Sztuka tłoczenia oliwy z oliwek extra virgin w sercu Apulii.”" : "\"Mastri Oleari dal 1926 – The art of pressing extra virgin olive oil in the heart of Apulia.\""}
            </motion.p>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 bg-[#E3E7D3]/40">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-[#5F7464] bg-[#5F7464]/10 px-3 py-1 rounded-full">
                  {language === 'PL' ? "Zielone Złoto Apulii" : "The Green Gold of Apulia"}
                </span>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-[#3A4F41]">{language === 'PL' ? "Dziedzictwo Frantoio Galantino" : "The Heritage of Frantoio Galantino"}</h2>
                <p className="text-lg leading-relaxed text-[#49504C] font-serif">
                  {language === 'PL' ? "Frantoio Galantino to renomowany, wielokrotnie nagradzany włoski producent oliwy z oliwek extra virgin. Sercem produkcji jest założona w 1960 roku farma „La Fenice”, na której zielonych polach rośnie ponad 15 000 szlachetnych drzew oliwnych odmian Ogliarola i Coratina. Wyjątkowa troska o jakość na każdym kroku owocuje oliwą o niezrównanej czystości, zbalansowanym smaku i bogatym śródziemnomorskim aromacie." : "Frantoio Galantino is a renowned, award-winning Italian producer of extra virgin olive oil. The heart of production is the \"La Fenice\" farm, founded in 1960, in whose green fields grow over 15,000 noble olive trees of the Ogliarola and Coratina varieties. Exceptional care for quality at every step results in oil of incomparable purity, balanced taste, and rich Mediterranean aroma."}
                </p>
                <div className="flex items-center gap-3 text-sm text-[#5F7464] font-semibold uppercase tracking-wider">
                  <MapPin className="text-[#879B87] w-5 h-5" />
                  <span>Bisceglie, Puglia, Włochy</span>
                </div>
              </div>
              <div className="flex justify-center">
                <img 
                  src="/suppliers/galantino/harvester.webp" 
                  alt="Zbiór oliwek Galantino" 
                  loading="lazy"
                  className="rounded-2xl shadow-xl border border-[#A3B19B]/30"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mastro Oleario Section (Gianvito Galantino) */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="grid md:grid-cols-5 gap-12 items-center">
              <div className="md:col-span-2 flex justify-center">
                <img 
                  src="/suppliers/galantino/gianvito.webp" 
                  alt="Gianvito Galantino" 
                  className="rounded-2xl shadow-xl border border-[#A3B19B]/20 max-h-96 object-cover"
                />
              </div>
              <div className="md:col-span-3 space-y-6">
                <span className="text-xs font-bold uppercase tracking-widest text-[#5F7464] bg-[#5F7464]/10 px-3 py-1 rounded-full">
                  Mastro Oleario
                </span>
                <h3 className="text-2xl md:text-3xl font-display font-bold text-[#3A4F41]">Gianvito Galantino</h3>
                <h4 className="text-lg font-serif italic text-foreground/75">{language === 'PL' ? "Trzecie pokolenie mistrzostwa" : "The third generation of mastery"}</h4>
                <p className="text-lg leading-relaxed text-[#49504C] font-serif">
                  {language === 'PL' ? "Gianvito Galantino reprezentuje trzecie pokolenie rodziny prowadzącej tłocznię. Przejął stery po swoim ojcu, Michele Galantino, który był odpowiedzialny za międzynarodowy sukces marki i kultowe ceramiczne opakowania." : "Gianvito Galantino represents the third generation of the family running the mill. He took the helm after his father, Michele Galantino, who was responsible for the brand's international success and iconic ceramic packaging."}
                </p>
                <p className="text-lg leading-relaxed text-[#49504C] font-serif">
                  {language === 'PL' ? "Gianvito kontynuuje rodzinną tradycję w Bisceglie, łącząc rolę menedżera z prestiżową funkcją **Mastro Oleario (mistrza olejarskiego)**. Osobiście nadzoruje proces selekcji oliwek i tłoczenia. Pod jego kreatywnym kierownictwem firma rozwinęła szeroką gamę produktów premium, w tym wyśmienite naturalne oliwy aromatyzowane." : "Gianvito continues the family tradition in Bisceglie, combining the role of a manager with the prestigious function of **Mastro Oleario (master olive oil maker)**. He personally oversees the olive selection and pressing process. Under his creative direction, the company has developed a wide range of premium products, including exquisite natural flavored oils."}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Story Section */}
        <section className="py-20 bg-[#F3F4ED]">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-center text-[#3A4F41] mb-8 sm:mb-16">
              {language === 'PL' ? "Nasza Droga od 1926 Roku" : "Our Path Since 1926"}
            </h2>

            <div className="relative border-l border-[#A3B19B]/40 md:border-l-0 md:before:absolute md:before:left-1/2 md:before:top-0 md:before:h-full md:before:w-0.5 md:before:bg-[#A3B19B]/40 space-y-16">
              {timelineEvents.map((event, index) => {
                const isEven = index % 2 === 0;
                return (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className={`relative pl-8 md:pl-0 md:grid md:grid-cols-2 md:gap-16 items-center ${isEven ? 'md:text-right' : ''}`}
                  >
                    {/* Timeline Node */}
                    <div className="absolute left-0 top-1.5 md:left-1/2 md:-translate-x-1/2 md:top-1/2 md:-translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-[#A3B19B]/50 bg-[#F3F4ED] shadow-md">
                      {event.icon}
                    </div>

                    {/* Timeline Content Block */}
                    <div className={`${isEven ? 'md:col-start-1' : 'md:col-start-2'} space-y-3`}>
                      <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#A3B19B]/15 text-[#3A4F41] rounded-full">
                        {event.year}
                      </span>
                      <h3 className="text-2xl font-bold text-[#3A4F41] font-display">{event.title}</h3>
                      <p className="text-[#49504C] leading-relaxed font-serif text-lg">{event.text}</p>
                    </div>

                    {/* Visual Asset Block */}
                    {event.image ? (
                      <div className={`mt-4 md:mt-0 ${isEven ? 'md:col-start-2' : 'md:col-start-1 md:row-start-1'} flex justify-center`}>
                        <motion.img 
                          whileHover={{ scale: 1.03 }}
                          src={event.image} 
                          alt={event.imgAlt} 
                          className="rounded-xl shadow-lg border border-[#A3B19B]/20 max-h-60 object-cover w-full max-w-sm"
                        />
                      </div>
                    ) : (
                      <div className="hidden md:block" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Metoda Produkcji Section */}
        <section className="py-20 bg-white border-t border-b border-[#A3B19B]/20">
          <div className="container mx-auto px-4 max-w-4xl space-y-16">
            <div className="text-center max-w-2xl mx-auto space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-[#5F7464] bg-[#5F7464]/10 px-3 py-1 rounded-full">
                {language === 'PL' ? "Sztuka Tłoczenia" : "The Art of Pressing"}
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-[#3A4F41]">{language === 'PL' ? "Metoda Produkcji Galantino" : "Galantino Production Method"}</h2>
              <p className="text-lg leading-relaxed text-[#49504C] font-serif">
                {language === 'PL' ? "Proces powstawania oliwy w Frantoio Galantino to harmonijny mariaż stuletniej tradycji i zaawansowanej nowoczesnej technologii." : "The process of creating olive oil at Frantoio Galantino is a harmonious marriage of a century-old tradition and advanced modern technology."}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="bg-[#5F7464]/10 p-3 rounded-xl text-[#3A4F41]">
                    <Heart className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#3A4F41] mb-2">{language === 'PL' ? "Ręczny zbiór" : "Manual harvest"}</h3>
                    <p className="text-foreground/80 leading-relaxed font-serif">
                      {language === 'PL' ? "Oliwki odmian Ogliarola i Coratina są starannie zbierane ręcznie lub przy użyciu delikatnych urządzeń bezpośrednio na specjalne tkaniny (sieci) w idealnym momencie dojrzałości – od 15 października do 31 grudnia." : "Ogliarola and Coratina olives are carefully picked by hand or using delicate devices directly onto special fabrics (nets) at the perfect moment of ripeness - from October 15 to December 31."}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="bg-[#5F7464]/10 p-3 rounded-xl text-[#3A4F41]">
                    <Cpu className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#3A4F41] mb-2">{language === 'PL' ? "Granitowe kamienie młyńskie" : "Granite millstones"}</h3>
                    <p className="text-foreground/80 leading-relaxed font-serif">
                      {language === 'PL' ? "Oliwki po dokładnym selekcjonowaniu i myciu są powoli miażdżone za pomocą potężnych, tradycyjnych granitowych kół młyńskich. Ta klasyczna metoda pozwala na delikatne uwolnienie olejów bez przegrzewania pasty oliwnej." : "After careful selection and washing, the olives are slowly crushed using powerful, traditional granite millwheels. This classic method allows for gentle release of the oils without overheating the olive paste."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="bg-[#5F7464]/10 p-3 rounded-xl text-[#3A4F41]">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#3A4F41] mb-2">{language === 'PL' ? "Ekstrakcja na zimno i wirowanie" : "Cold extraction and centrifugation"}</h3>
                    <p className="text-foreground/80 leading-relaxed font-serif">
                      {language === 'PL' ? "Powstała pasta oliwna podlega ekstrakcji na zimno (zawsze w temperaturze poniżej 27°C, zazwyczaj około 23°C). W specjalnych centrifugach (wirówkach) następuje całkowite oddzielenie złocistej oliwy od wody roślinnej i części stałych (wytłoków)." : "The resulting olive paste undergoes cold extraction (always at a temperature below 27°C, usually around 23°C). In special centrifuges, the golden oil is completely separated from the plant water and solid parts (pomace)."}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="bg-[#5F7464]/10 p-3 rounded-xl text-[#3A4F41]">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#3A4F41] mb-2">{language === 'PL' ? "Kontrola Mastro Oleario" : "Mastro Oleario control"}</h3>
                    <p className="text-foreground/80 leading-relaxed font-serif">
                      {language === 'PL' ? "Każda partia oliwy przechodzi przez rygorystyczne testy laboratoryjne oraz pełne badania organoleptyczne. Dopiero po uzyskaniu aprobaty mistrza olejarskiego, produkt otrzymuje ostateczną certyfikację i trafia do butelek." : "Each batch of oil goes through rigorous laboratory tests and full organoleptic tests. Only after obtaining the approval of the master olive oil maker, the product receives final certification and is bottled."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Action Button */}
        <div className="container mx-auto px-4 max-w-4xl text-center pb-12 pt-8">
            <a href="https://www.galantino.it" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#3A4F41] text-white font-bold py-3 px-8 rounded-full hover:bg-[#3A4F41]/90 transition-colors shadow-lg">
                {language === 'PL' ? "Dowiedz się więcej" : "Learn more"}
            </a>
        </div>

        {/* Gallery Section */}
        <ImageGallery 
          images={[
            "/suppliers/galantino/gallery/g-1.webp",
            "/suppliers/galantino/gallery/g-2.webp",
            "/suppliers/galantino/gallery/g-3.webp",
            "/suppliers/galantino/gallery/g-4.webp",
            "/suppliers/galantino/gallery/g-5.webp",
            "/suppliers/galantino/gallery/g-6.webp",
            "/suppliers/galantino/gallery/g-7.webp",
            "/suppliers/galantino/gallery/g-8.webp"
          ]} 
        />
      </main>

      <Footer />
    </div>
  );
};

export default SupplierGalantinoPage;
